import { assert, warn } from '@ember/debug';
import { next } from '@ember/runloop';
import { getOwnConfig, isDevelopingApp, macroCondition } from '@embroider/macros';
import * as Sentry from '@sentry/browser';
import { SDK_VERSION } from '@sentry/browser';
import { GLOBAL_OBJ, timestampInSeconds } from '@sentry/utils';
import Ember from 'ember';
function _getSentryInitConfig() {
  const _global = GLOBAL_OBJ;
  _global.__sentryEmberConfig = _global.__sentryEmberConfig ?? {};
  return _global.__sentryEmberConfig;
}
export function InitSentryForEmber(_runtimeConfig) {
  const environmentConfig = getOwnConfig().sentryConfig;
  assert('Missing configuration.', environmentConfig);
  assert('Missing configuration for Sentry.', environmentConfig.sentry || _runtimeConfig);
  if (!environmentConfig.sentry) {
    // If environment config is not specified but the above assertion passes, use runtime config.
    environmentConfig.sentry = {
      ..._runtimeConfig
    };
  }

  // Merge runtime config into environment config, preferring runtime.
  Object.assign(environmentConfig.sentry, _runtimeConfig || {});
  const initConfig = Object.assign({}, environmentConfig.sentry);
  initConfig._metadata = initConfig._metadata || {};
  initConfig._metadata.sdk = {
    name: 'sentry.javascript.ember',
    packages: [{
      name: 'npm:@sentry/ember',
      version: SDK_VERSION
    }],
    version: SDK_VERSION
  };

  // Persist Sentry init options so they are identical when performance initializers call init again.
  const sentryInitConfig = _getSentryInitConfig();
  Object.assign(sentryInitConfig, initConfig);
  Sentry.init(initConfig);
  if (macroCondition(isDevelopingApp())) {
    if (environmentConfig.ignoreEmberOnErrorWarning) {
      return;
    }
    next(null, function () {
      warn('Ember.onerror found. Using Ember.onerror can hide some errors (such as flushed runloop errors) from Sentry. Use Sentry.captureException to capture errors within Ember.onError or remove it to have errors caught by Sentry directly. This error can be silenced via addon configuration.', !Ember.onerror, {
        id: '@sentry/ember.ember-onerror-detected'
      });
    });
  }
}
export const getActiveTransaction = () => {
  return Sentry.getCurrentHub().getScope().getTransaction();
};
export const instrumentRoutePerformance = BaseRoute => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const instrumentFunction = async (op, description, fn, args) => {
    const startTimestamp = timestampInSeconds();
    const result = await fn(...args);
    const currentTransaction = getActiveTransaction();
    if (!currentTransaction) {
      return result;
    }
    currentTransaction.startChild({
      op,
      description,
      origin: 'auto.ui.ember',
      startTimestamp
    }).finish();
    return result;
  };
  const routeName = BaseRoute.name;
  return {
    // @ts-expect-error TS2545 We do not need to redefine a constructor here
    [routeName]: class extends BaseRoute {
      beforeModel(...args) {
        return instrumentFunction('ui.ember.route.before_model', this.fullRouteName, super.beforeModel.bind(this), args);
      }
      async model(...args) {
        return instrumentFunction('ui.ember.route.model', this.fullRouteName, super.model.bind(this), args);
      }
      afterModel(...args) {
        return instrumentFunction('ui.ember.route.after_model', this.fullRouteName, super.afterModel.bind(this), args);
      }
      setupController(...args) {
        return instrumentFunction('ui.ember.route.setup_controller', this.fullRouteName, super.setupController.bind(this), args);
      }
    }
  }[routeName];
};
export * from '@sentry/browser';

// init is now the preferred way to call initialization for this addon.
export const init = InitSentryForEmber;